import { defaultNS, namespaces, translations } from '@generated/i18n.generated';
import type { BareLocalization } from '@interfaces/localization';
import type { TranslatedString } from '@interfaces/shared';
import {
  CountryCodes,
  CurrencyCodes,
  LanguageCodes,
  type Languages,
} from '@onevesthq/ov-enums';
import {
  createTranslationFunctions,
  languageCodeToLanguageMap,
  languageToLanguageCodeMap,
} from '@onevesthq/ov-translations';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
// Add dayjs locale import when adding new LanguageCodes
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';

export { defaultNS, namespaces, translations };

/**
 * Dynamic language fields for GraphQL queries. Automatically includes all supported languages.
 * Example: translatedName { ${TRANSLATED_STRING_FIELDS} } → translatedName { en fr es }
 */
export const TRANSLATED_STRING_FIELDS = Object.values(LanguageCodes).join(' ');

/* Fallback defaults when no localization is set */
export const LOCALIZATION_DEFAULTS: BareLocalization = {
  name: 'defaults',
  countries: [CountryCodes.CA],
  phoneNumberCountryCodes: [],
  languages: Object.values(LanguageCodes),
  defaultCurrency: CurrencyCodes.CAD,
  defaultLanguage: LanguageCodes.en,
  dateFormat: 'YYYY-MM-DD',
};

// To add a new language (e.g., Spanish):
// 1. Create src/assets/i18n/es/ folder with translation files
// 2. Run `pnpm gen:i18n` (or automatic on next `pnpm install`)

i18n.use(LanguageDetector).init();
i18n.use(initReactI18next).init({
  // Initializes with LOCALIZATION_DEFAULTS at app start.
  fallbackLng: LOCALIZATION_DEFAULTS.defaultLanguage,
  ns: namespaces,
  defaultNS,
  interpolation: { escapeValue: false },
  resources: translations,
});

/**
 * Validate a locale string using Intl.Locale (BCP 47 standard)
 */
const isValidLocale = (locale: string): boolean => {
  try {
    new Intl.Locale(locale);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get locale string for current user (for Intl.NumberFormat, etc.)
 * @param country - Optional country code. Defaults to US (matches current 'en-US' behavior)
 * @returns BCP 47 locale string (e.g., 'en-US', 'fr-CA', 'es-ES')
 */
export const getLocaleString = (country?: CountryCodes): string => {
  const lang = getBackendLanguage();
  const countryCode = country || CountryCodes.US;
  const locale = `${lang}-${countryCode}`;

  return isValidLocale(locale) ? locale : 'en-US';
};

export const getBackendLanguage = (): LanguageCodes => {
  const lang = i18n.language as LanguageCodes;
  return Object.values(LanguageCodes).includes(lang)
    ? lang
    : getOrgDefaultLanguage();
};

export const setBackendLanguage = (language: Languages) => {
  const langCode = languageToLanguageCodeMap[language];
  i18n.changeLanguage(langCode);
};

/**
 * Read org localization from localStorage (with safe fallback)
 */
const getOrgLocalization = (): BareLocalization => {
  try {
    const orgData = JSON.parse(
      localStorage.getItem('activeOrganizationData') || '{}',
    );
    return orgData.applicableLocalization || LOCALIZATION_DEFAULTS;
  } catch {
    return LOCALIZATION_DEFAULTS;
  }
};

/**
 * Update org's localization in localStorage
 */
export const updateOrgLocalizationInStorage = (
  localization: BareLocalization,
): void => {
  if (!localization) return;

  try {
    const orgData = JSON.parse(
      localStorage.getItem('activeOrganizationData') || '{}',
    );
    orgData.applicableLocalization = localization;
    localStorage.setItem('activeOrganizationData', JSON.stringify(orgData));
  } catch (error) {
    console.error('Failed to update localization in localStorage:', error);
  }
};

/**
 * Get org's default language
 * @param options.asEnum - Return as Languages enum (e.g., 'ENGLISH') for legacy GraphQL mutations.
 * Default returns LanguageCodes (e.g., 'en') which is the preferred format.
 */
export function getOrgDefaultLanguage(): LanguageCodes;
export function getOrgDefaultLanguage(options: { asEnum: true }): Languages;
export function getOrgDefaultLanguage(options?: {
  asEnum?: boolean;
}): LanguageCodes | Languages {
  const localization = getOrgLocalization();
  const code = localization.defaultLanguage as LanguageCodes;
  return options?.asEnum ? languageCodeToLanguageMap[code] : code;
}

/**
 * Get org's enabled languages as LanguageCodes (e.g., ['en', 'fr'])
 * Use LanguageCodes for all new code.
 */
export const getOrgLanguageCodes = (): LanguageCodes[] => {
  return getOrgLocalization().languages;
};

/**
 * Get org's enabled languages as Languages enum (e.g., ['ENGLISH', 'FRENCH'])
 * Languages enum is legacy - only use where required (e.g., GraphQL mutations, user language dropdowns).
 */
export const getOrgLanguages = (): Languages[] => {
  return getOrgLanguageCodes().map((code) => languageCodeToLanguageMap[code]);
};

/**
 * Get display name for a language using Intl.DisplayNames
 * Supports both LanguageCodes (en/fr/es) and Languages enum (ENGLISH/FRENCH)
 * @returns Localized language name with first letter capitalized (e.g., "French" in English, "Français" in French)
 */
export const getLanguageDisplayName = (
  lang: LanguageCodes | Languages,
): string => {
  const code = Object.values(LanguageCodes).includes(lang as LanguageCodes)
    ? (lang as LanguageCodes)
    : languageToLanguageCodeMap[lang as Languages];

  const displayNames = new Intl.DisplayNames([getBackendLanguage()], {
    type: 'language',
  });
  const name = displayNames.of(code) ?? code ?? '';
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const translateBackend = (translatedObject: any): string => {
  const userLang = getBackendLanguage();
  return (
    translatedObject?.[userLang] ||
    translatedObject?.[getOrgDefaultLanguage()] ||
    ''
  );
};

export type FormCompletionNameFields = {
  resolvedPdfFileName?: TranslatedString | null;
  translatedName?: TranslatedString | null;
};

export const translateFormCompletionName = (
  formCompletion: FormCompletionNameFields,
): string =>
  translateBackend(formCompletion.resolvedPdfFileName) ||
  translateBackend(formCompletion.translatedName);

/**
 * Returns true if any language has non-empty content.
 */
export const hasAnyTranslation = (obj: any): boolean => {
  if (!obj) return false;
  return Object.values(LanguageCodes).some((code) => obj[code]?.trim());
};

/** Returns true if no language has content (inverse of hasAnyTranslation) */
export const isTranslatableStringEmpty = (obj: any): boolean => {
  return !hasAnyTranslation(obj);
};

/** Returns true if any language value exceeds the max length */
export const anyTranslationExceedsLength = (
  obj: TranslatedString,
  maxLength: number,
): boolean => {
  if (!obj) return false;
  return Object.values(obj).some(
    (val) => typeof val === 'string' && val.length > maxLength,
  );
};

/** Extracts only language keys that exist in source (filters out __typename, etc) */
export const extractTranslations = (obj: any): TranslatedString => {
  if (!obj) return {} as TranslatedString;
  return Object.fromEntries(
    Object.values(LanguageCodes)
      .filter((lang) => lang in obj)
      .map((lang) => [lang, obj[lang] ?? '']),
  ) as TranslatedString;
};

export const trimTranslatedStrings = <
  T extends Record<string, string | undefined>,
>(
  field: T,
): T =>
  Object.fromEntries(
    Object.entries(field).map(([k, v]) => [k, v?.trim()]),
  ) as T;

/**
 * @param fileName - The name of the translation file (e.g., 'netWorth', 'authentication')
 * @param translationPath - Array of keys to navigate the nested object
 * @param variables - Optional variables for string interpolation (can be string or TranslatedString)
 * @returns Object with translations for all languages
 */
export const { getTranslations } = createTranslationFunctions(
  // Cast through unknown since we may not have all languages yet (e.g., es)
  translations as unknown as Parameters<typeof createTranslationFunctions>[0],
);

export const emptyTranslation = getTranslations({
  fileName: '',
  translationPath: [],
});

export default i18n;
